import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KoalaMark } from '../components/KoalaMark';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Button, ErrorNote, Field } from '../components/ui';
import { api, authClient } from '../lib/api';
import { useAuthError } from '../lib/auth-errors';
import { CodeField } from '../components/TwoFactor';
import { useLocale, useT } from '../lib/i18n';

export function AuthShell({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const t = useT();
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:bg-frame lg:bg-[radial-gradient(640px_520px_at_10%_0%,rgb(245_228_184/0.16),transparent_70%)] lg:p-2.5">
      {/* Desktop: the promise on the dark desk, with the appointment card your clients will get. */}
      <aside className="hidden flex-col justify-between p-12 text-canvas lg:flex">
        <span className="flex items-center gap-3">
          <span className="flex size-11 items-center justify-center rounded-[14px] bg-frame-soft shadow-[0_0_28px_rgb(245_228_184/0.22)] ring-1 ring-white/10">
            <KoalaMark className="size-8" />
          </span>
          <span className="display text-[26px]">KoalaSpot</span>
        </span>
        <div className="mx-auto flex w-full max-w-[620px] flex-col gap-8 [@media(min-height:860px)]:gap-10 [@media(min-height:860px)]:2xl:gap-14">
          <p className="display max-w-[13ch] text-[68px] leading-[0.95] 2xl:text-[88px]">{t('auth.side.title')}</p>
          {/* A glimpse of the product: a day in the agenda, with the card a client gets on top. */}
          <div aria-hidden className="relative [@media(min-height:860px)]:h-[300px] [@media(min-height:860px)]:2xl:h-[340px]">
            <div className="absolute left-10 top-0 hidden w-[340px] rotate-3 [@media(min-height:860px)]:block rounded-[20px] bg-frame-soft p-4 shadow-[0_30px_60px_rgb(0_0_0/0.3)] ring-1 ring-canvas/10 2xl:left-16 2xl:w-[380px]">
              <p className="eyebrow !text-canvas/50">{t('auth.day.title')}</p>
              <ul className="mt-3 flex flex-col gap-2">
                {[
                  { time: '09:30', name: 'Noor', what: t('auth.day.1'), color: '#7C5CBF' },
                  { time: '10:30', name: 'Emma', what: t('auth.day.2'), color: '#C2562F' },
                  { time: '11:45', name: 'Daan', what: t('auth.day.3'), color: '#2F6FA8' },
                ].map((r) => (
                  <li key={r.time} className="flex items-center gap-3 rounded-[12px] border-l-[3px] px-3 py-2 text-[13px]" style={{ borderLeftColor: r.color, backgroundColor: `color-mix(in srgb, ${r.color} 22%, transparent)` }}>
                    <span className="tabular-nums text-canvas/60">{r.time}</span>
                    <span className="font-semibold">{r.name}</span>
                    <span className="text-canvas/60">{r.what}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative w-[380px] -rotate-2 [@media(min-height:860px)]:absolute [@media(min-height:860px)]:bottom-0 [@media(min-height:860px)]:left-0 overflow-hidden rounded-[20px] bg-surface text-ink shadow-[0_30px_60px_rgb(0_0_0/0.35)] 2xl:w-[420px]">
              <div className="flex">
                <div className="flex w-24 flex-col items-center justify-center gap-1 bg-subtle py-5">
                  <span className="eyebrow">{t('auth.card.weekday')}</span>
                  <span className="display text-[50px] leading-[0.85]">14</span>
                  <span className="text-sm text-muted">{t('auth.card.month')}</span>
                </div>
                <div className="flex flex-col gap-0.5 p-5">
                  <span className="text-[17px] font-semibold">10:30–11:15</span>
                  <span>{t('auth.card.service')}</span>
                  <span className="text-muted">{t('auth.card.with')}</span>
                </div>
              </div>
              <div className="flex justify-between border-t border-dashed border-line px-5 py-2.5">
                <span className="eyebrow">{t('auth.card.label')}</span>
                <span className="text-sm text-muted">€ 45,00</span>
              </div>
            </div>
          </div>
          <ul className="grid gap-3 text-[15px] text-canvas/75 2xl:grid-cols-3">
            {(['auth.point.book', 'auth.point.remind', 'auth.point.free'] as const).map((k) => (
              <li key={k} className="flex items-start gap-2.5">
                <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-cream shadow-[0_0_10px_rgb(245_228_184/0.6)]" />
                {t(k)}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-canvas/40">{t('auth.side.foot')}</p>
      </aside>
      <main className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-10 lg:min-h-0 lg:rounded-[var(--radius-panel)]">
        <div className="w-full max-w-[420px] 2xl:max-w-[460px]">
          <span className="mb-8 flex items-center gap-2.5 lg:hidden">
            <KoalaMark className="size-10" />
            <span className="display text-[24px]">KoalaSpot</span>
          </span>
          <h1 className="page-title text-balance">{title}</h1>
          {subtitle && <p className="mt-3 text-muted">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}

export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uitnodiging } = useSearch({ strict: false }) as { uitnodiging?: string };
  const authError = useAuthError();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** After the password: the second step, with the app code or a backup code. */
  const [second, setSecond] = useState<null | 'app' | 'backup'>(null);

  function done() {
    queryClient.clear();
    // Came from an invitation: join that salon first.
    if (uitnodiging) return navigate({ to: '/uitnodiging', search: { token: uitnodiging } });
    navigate({ to: '/' });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    const { data, error } = await authClient.signIn.email({
      email: String(form.get('email')),
      password: String(form.get('password')),
    });
    setBusy(false);
    if (error) return setError(authError(error));
    if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) return setSecond('app');
    done();
  }

  async function onCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const code = String(form.get('code')).trim();
    const trustDevice = form.get('trust') === 'on';
    setBusy(true);
    setError(null);
    const { error } =
      second === 'backup' ? await authClient.twoFactor.verifyBackupCode({ code, trustDevice }) : await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, ''), trustDevice });
    setBusy(false);
    if (!error) return done();
    // The 10-minute window to enter a code is over: start again from the password.
    if (error.code === 'INVALID_TWO_FACTOR_COOKIE' || error.code?.startsWith('TOO_MANY_ATTEMPTS')) setSecond(null);
    setError(authError(error));
  }

  if (second) {
    return (
      <AuthShell title={t('login.codeTitle')} subtitle={second === 'app' ? t('login.codeIntro') : t('login.backupIntro')}>
        <form key={second} onSubmit={onCode} className="flex flex-col gap-4">
          <CodeField id="code" label={second === 'app' ? t('tf.code') : t('login.backupCode')} backup={second === 'backup'} />
          <label className="flex items-center gap-2 text-[15px]">
            <input type="checkbox" name="trust" className="size-5 accent-[var(--color-accent)]" />
            {t('login.trust')}
          </label>
          <ErrorNote message={error} />
          <Button type="submit" disabled={busy}>
            {t('auth.login.submit')}
          </Button>
          <button
            type="button"
            className="self-start text-sm font-semibold text-accent-strong underline-offset-2 hover:underline"
            onClick={() => {
              setError(null);
              setSecond(second === 'app' ? 'backup' : 'app');
            }}
          >
            {second === 'app' ? t('login.useBackup') : t('login.useApp')}
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.login.title')}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field id="email" name="email" type="email" autoComplete="email" required label={t('field.email')} />
        <Field id="password" name="password" type="password" autoComplete="current-password" required label={t('field.password')} />
        <Link to="/wachtwoord-vergeten" className="-mt-2 self-start text-sm font-semibold text-accent-strong underline-offset-2 hover:underline">
          {t('auth.forgot')}
        </Link>
        <ErrorNote message={error} />
        <Button type="submit" disabled={busy}>
          {t('auth.login.submit')}
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted">
        {t('auth.login.noAccount')}{' '}
        <Link to="/aanmelden" className="font-semibold text-accent-strong underline-offset-2 hover:underline">
          {t('auth.login.tryFree')}
        </Link>
      </p>
    </AuthShell>
  );
}

type SignupInfo = { open: boolean; trialUrl: string | null };

/** Sign-up: in the app while it is open (development); otherwise the free trial starts at the checkout. */
export function SignupPage() {
  const t = useT();
  const info = useQuery({ queryKey: ['signup-info'], queryFn: () => api<SignupInfo>('/public/signup'), staleTime: 300_000 });
  if (!info.data) return <AuthShell title={t('auth.signup.title')} subtitle=" ">{info.isError ? <SignupForm /> : null}</AuthShell>;
  if (info.data.open || !info.data.trialUrl) return <SignupForm />;
  return (
    <AuthShell title={t('trial.cta.title')} subtitle={t('trial.cta.subtitle')}>
      <div className="flex flex-col gap-4">
        <ul className="flex list-disc flex-col gap-1.5 pl-5 text-[15px]">
          {(['trial.cta.p1', 'trial.cta.p2', 'trial.cta.p3'] as const).map((k) => (
            <li key={k}>{t(k)}</li>
          ))}
        </ul>
        <a href={info.data.trialUrl} className="inline-flex min-h-12 items-center justify-center rounded-full bg-accent px-6 text-[16px] font-semibold text-on-accent hover:bg-accent-strong">
          {t('trial.cta.button')}
        </a>
        <p className="text-sm text-muted">{t('trial.cta.note')}</p>
      </div>
    </AuthShell>
  );
}

/** The thank-you page after the trial checkout (no account yet: the invitation arrives by email). */
export function TrialStartedPage() {
  const t = useT();
  return (
    <AuthShell title={t('trial.done.title')} subtitle={t('trial.done.subtitle')}>
      <p className="text-[15px] text-muted">{t('trial.done.body')}</p>
    </AuthShell>
  );
}

function SignupForm() {
  const t = useT();
  const authError = useAuthError();
  const locale = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    const { error } = await authClient.signUp.email({
      name: String(form.get('name')),
      email: String(form.get('email')),
      password: String(form.get('password')),
      locale,
    } as Parameters<typeof authClient.signUp.email>[0]);
    setBusy(false);
    if (error) return setError(authError(error));
    queryClient.clear();
    navigate({ to: '/start' });
  }

  return (
    <AuthShell title={t('auth.signup.title')} subtitle={t('auth.signup.subtitle')}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field id="name" name="name" autoComplete="name" required label={t('field.name')} />
        <Field id="email" name="email" type="email" autoComplete="email" required label={t('field.email')} />
        <Field
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          label={t('field.password')}
          hint={t('field.passwordHint')}
        />
        <ErrorNote message={error} />
        <Button type="submit" disabled={busy}>
          {t('auth.signup.submit')}
        </Button>
        <p className="text-sm text-muted">
          {t('auth.signup.terms')}{' '}
          <a href="/juridisch/voorwaarden" target="_blank" rel="noreferrer" className="font-semibold text-accent-strong hover:underline">
            {t('legal.terms')}
          </a>{' '}
          ·{' '}
          <a href="/juridisch/verwerkersovereenkomst" target="_blank" rel="noreferrer" className="font-semibold text-accent-strong hover:underline">
            {t('legal.dpa')}
          </a>{' '}
          ·{' '}
          <a href="/juridisch/privacy" target="_blank" rel="noreferrer" className="font-semibold text-accent-strong hover:underline">
            {t('legal.privacy')}
          </a>
        </p>
      </form>
      <p className="mt-6 text-sm text-muted">
        {t('auth.signup.haveAccount')}{' '}
        <Link to="/inloggen" className="font-semibold text-accent-strong underline-offset-2 hover:underline">
          {t('auth.login.submit')}
        </Link>
      </p>
    </AuthShell>
  );
}
